import { boot } from '@dazn/bootstrap-lib';
import { enums as optimizelyEnums } from '@optimizely/optimizely-sdk';
export const FV_CONSUMER_DEFINED_LOG_LEVEL_STORAGE_KEY = 'featurevisorLogLevel';
export const O_CONSUMER_DEFINED_LOG_LEVEL_STORAGE_KEY = 'optimizelyLogLevel';

type InstanceType = 'optimizely' | 'featurevisor';

const VALID_STRING_LOG_LEVELS: boot.optimizely.ConsumerDefinedLogLevel[] = [
    'info',
    'debug',
    'warn',
    'error',
];

const isValidLogLevel = (
    type: InstanceType,
    level:
        | boot.optimizely.ConsumerDefinedLogLevel
        | boot.optimizely.ConsumerDefinedLogLevel[],
) =>
    type === 'optimizely' && typeof level === 'string'
        ? VALID_STRING_LOG_LEVELS.includes(level)
        : type === 'featurevisor' && Array.isArray(level)
        ? level.every(l => VALID_STRING_LOG_LEVELS.includes(l))
        : false;

const getInstanceWarn = (instance: InstanceType) => {
    if (instance === 'optimizely') {
        return optimizelyEnums.LOG_LEVEL.WARNING;
    }
    return ['warn'];
};

const getInstanceError = (instance: InstanceType) => {
    if (instance === 'optimizely') {
        return optimizelyEnums.LOG_LEVEL.ERROR;
    }
    return ['error'];
};

export const getLogLevel = ({
    asyncStorage,
    level,
    isDevMode,
    isProd,
    type,
}: {
    asyncStorage: boot.storage.AsyncStorage;
    level:
        | boot.optimizely.ConsumerDefinedLogLevel
        | boot.optimizely.ConsumerDefinedLogLevel[]
        | null;
    isDevMode: boolean;
    isProd: boolean;
    type: InstanceType;
}) => {
    const logLevelStorageKey =
        type === 'featurevisor'
            ? FV_CONSUMER_DEFINED_LOG_LEVEL_STORAGE_KEY
            : O_CONSUMER_DEFINED_LOG_LEVEL_STORAGE_KEY;
    return asyncStorage
        .getItem(logLevelStorageKey)
        .then(cachedLogLevel => {
            if (type === 'featurevisor') {
                return cachedLogLevel && JSON.parse(cachedLogLevel);
            } else {
                return cachedLogLevel;
            }
        })
        .then(cachedLogLevel => {
            if (cachedLogLevel && !isDevMode) {
                asyncStorage.removeItem(logLevelStorageKey);
            }

            if (!level || !level.length) {
                return isProd
                    ? getInstanceError(type)
                    : cachedLogLevel && isDevMode
                    ? cachedLogLevel
                    : getInstanceWarn(type);
            }

            if (!isDevMode) {
                console.warn(
                    '[Bootstrap] You must have devMode enabled to use this feature.',
                );
                return;
            }

            if (!isValidLogLevel(type, level)) {
                console.warn(
                    "[Bootstrap] Please provide a valid log level to use this feature (can be 'info', 'debug', 'warn', 'error').",
                );
                return;
            }

            asyncStorage.setItem(
                logLevelStorageKey,
                typeof level === 'string' ? level : JSON.stringify(level),
            );
            return level;
        });
};
